/**
 * SalesOrderDetailPage (S2.3) — SO detail restyled on the VendorDetailPage
 * trio pattern (ProductDetailPage precedent): SoHeader card (status Tag,
 * external order #, pending-approval banner w/ ROLE-GATED release) + 248px
 * Sider section menu + section content.
 *
 * - `?section=` deep-link: the active section is ROUTE-DERIVED (query
 *   param), never local-only state (house selection tenet).
 * - Sections: 訂單明細 lines (trace popovers over the persisted immutable
 *   priceTrace + linked-PO chips rail, populated by W3) · 確認檢核 confirm
 *   checklist card · 稽核 audit.
 * - Modification is the SEPARATE modify flow (SalesOrderEditPage — SO-PO
 *   board: 一條建立、一條修改); this page only links to it.
 * - Release goes through ReasonRequiredModal (mandatory logged reason);
 *   confirm failures surface the SERVER checklist on the checklist card.
 */

import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { App, Card, Grid, Layout, Menu, Spin, theme } from 'antd';
import { AuditOutlined, CheckSquareOutlined, ProfileOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ListErrorState } from '@/components/common/TableContainer';
import ReasonRequiredModal from '@/components/common/ReasonRequiredModal';
import {
  extractApiError,
  useReadyProducts,
  useSalesOrder,
  useSalesOrderMutations,
} from '@/hooks/useSalesOrders';
import { useCustomerOptions } from '@/hooks/useCustomers';
import { useFeature } from '@/features/flags';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';
import type { ConfirmChecklistResult } from '@/types/api/salesOrder';
import SoHeader from './SoHeader';
import LinesSection from './sections/LinesSection';
import ChecklistSection from './sections/ChecklistSection';
import AuditSection from './sections/AuditSection';
import { computeConfirmChecklist } from './checklist';
import {
  canReleasePriceHold,
  extractConfirmChecklist,
  useSalesOrderDetailActions,
} from './useSalesOrderDetailActions';
import { SO_DETAIL_SECTION_KEYS } from './types';
import type { SoDetailSectionKey } from './types';
import PageContainer from '@/components/common/PageContainer';

const { Sider, Content } = Layout;

const SECTION_ICONS: Record<SoDetailSectionKey, React.ReactNode> = {
  lines: <ProfileOutlined />,
  checklist: <CheckSquareOutlined />,
  audit: <AuditOutlined />,
};

const SalesOrderDetailPage = () => {
  const { t } = useTranslation(['orders', 'common']);
  const { soId } = useParams<{ soId: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const elevation = useElevation();
  const screens = Grid.useBreakpoint();
  const poFromSo = useFeature('procurement.poFromSo');

  // ?section= deep-link — route-derived selection (falls back to 'lines')
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionParam = searchParams.get('section');
  const activeSection: SoDetailSectionKey = SO_DETAIL_SECTION_KEYS.includes(
    sectionParam as SoDetailSectionKey
  )
    ? (sectionParam as SoDetailSectionKey)
    : 'lines';

  const setActiveSection = (section: SoDetailSectionKey) => {
    setSearchParams(section === 'lines' ? {} : { section });
  };

  const { salesOrder: so, isLoading, error, refresh } = useSalesOrder(soId);

  // Checklist-mirror feeds — the same Ready feed that gates the SO picker,
  // and the active-customer options feed.
  const { products: readyProducts, isLoading: readyLoading } = useReadyProducts();
  const { customers, isLoading: customersLoading } = useCustomerOptions();

  const { confirmSalesOrder, releasePriceHold } = useSalesOrderDetailActions();
  const { createPoFromSo } = useSalesOrderMutations();
  const canRelease = canReleasePriceHold();

  const [confirming, setConfirming] = useState(false);
  const [creatingPo, setCreatingPo] = useState(false);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [serverChecklist, setServerChecklist] = useState<ConfirmChecklistResult | null>(null);

  const feedsLoading = readyLoading || customersLoading;
  const localChecklist = useMemo(() => {
    if (!so || feedsLoading) return null;
    return computeConfirmChecklist(
      so,
      new Set(readyProducts.map((product) => product.id)),
      new Set(customers.map((customer) => customer.customerId))
    );
  }, [so, readyProducts, customers, feedsLoading]);

  const handleConfirm = async () => {
    if (!so) return;
    setConfirming(true);
    try {
      await confirmSalesOrder(so.id);
      setServerChecklist(null);
      message.success(t('orders:so.detail.confirm.success', { soNumber: so.soNumber }));
      refresh();
    } catch (err) {
      const checklist = extractConfirmChecklist(err);
      if (checklist) {
        // Authoritative server evaluation — surface it on the checklist card.
        setServerChecklist(checklist);
        setActiveSection('checklist');
        message.error(t('orders:so.detail.confirm.failed'));
      } else {
        const { message: detail } = extractApiError(err);
        message.error(detail || t('common:messages.updateFailed'));
      }
    } finally {
      setConfirming(false);
    }
  };

  const handleRelease = async (reason: string) => {
    if (!so) return;
    try {
      await releasePriceHold(so.id, reason);
      setReleaseOpen(false);
      message.success(t('orders:so.detail.pendingBanner.releasedMessage'));
      refresh();
    } catch (err) {
      const { message: detail } = extractApiError(err);
      message.error(detail || t('common:messages.updateFailed'));
      return false;
    }
  };

  const handleCreatePo = async () => {
    if (!so) return;
    setCreatingPo(true);
    try {
      const res = await createPoFromSo(so.id);
      message.success(t('orders:so.messages.poCreated', { count: res.createdCount }));
      const first = res.data[0];
      if (first) navigate(`/auth/purchase-orders/${first.id}`);
      else refresh();
    } catch (err) {
      const { message: detail } = extractApiError(err);
      message.error(detail || t('common:messages.createFailed'));
    } finally {
      setCreatingPo(false);
    }
  };

  if (error) {
    return (
      <PageContainer>
        <Card style={{ borderRadius: radiusTokens.panel }}>
          <ListErrorState
            title={t('orders:so.detail.loadFailed')}
            retryLabel={t('common:errors.boundary.retry')}
            onRetry={refresh}
          />
        </Card>
      </PageContainer>
    );
  }

  const menuItems = SO_DETAIL_SECTION_KEYS.map((key) => ({
    key,
    icon: SECTION_ICONS[key],
    label: t(`orders:so.detail.sections.${key}`),
  }));

  const renderSection = () => {
    if (!so) return null;
    switch (activeSection) {
      case 'lines':
        return <LinesSection so={so} />;
      case 'checklist':
        return (
          <ChecklistSection
            so={so}
            localChecklist={localChecklist}
            serverChecklist={serverChecklist}
            feedsLoading={feedsLoading}
          />
        );
      case 'audit':
        return <AuditSection so={so} />;
    }
  };

  return (
    <PageContainer>
      <Spin spinning={isLoading}>
        {so && (
          <>
            <SoHeader
              so={so}
              canRelease={canRelease}
              confirming={confirming}
              creatingPo={creatingPo}
              showCreatePo={poFromSo && so.status === 'confirmed' && so.priceStatus === 'ok'}
              onConfirm={handleConfirm}
              onRelease={() => setReleaseOpen(true)}
              onCreatePo={handleCreatePo}
            />

            <Card
              style={{
                ...elevation.card,
                overflow: 'hidden',
              }}
              styles={{ body: { padding: 0 } }}
            >
              <Layout style={{ backgroundColor: 'transparent' }}>
                {screens.lg && (
                  <Sider
                    width={248}
                    theme="light"
                    style={{
                      borderRight: `1px solid ${token.colorBorderSecondary}`,
                      padding: 0,
                      backgroundColor: 'transparent',
                    }}
                  >
                    <style>
                      {`
                        .so-detail-menu {
                          padding: 0 12px !important;
                        }
                        .so-detail-menu .ant-menu-item {
                          height: ${controlHeightTokens.controlHeightXL}px !important;
                          line-height: ${controlHeightTokens.controlHeightXL}px !important;
                          margin: 4px 0 !important;
                          display: flex !important;
                          align-items: center !important;
                          border-radius: ${radiusTokens.card}px !important;
                          width: 100% !important;
                        }
                      `}
                    </style>
                    <Menu
                      mode="inline"
                      selectedKeys={[activeSection]}
                      items={menuItems}
                      onClick={({ key }) => setActiveSection(key as SoDetailSectionKey)}
                      className="so-detail-menu"
                      style={{ borderRight: 'none', backgroundColor: 'transparent' }}
                    />
                  </Sider>
                )}
                <Content style={{ padding: '24px', minHeight: '400px', minWidth: 0 }}>
                  {!screens.lg && (
                    <Menu
                      mode="horizontal"
                      selectedKeys={[activeSection]}
                      items={menuItems}
                      onClick={({ key }) => setActiveSection(key as SoDetailSectionKey)}
                      style={{ marginBottom: token.marginLG }}
                    />
                  )}
                  {renderSection()}
                </Content>
              </Layout>
            </Card>

            {/* Role-gated release — mandatory logged reason (audit lens) */}
            <ReasonRequiredModal
              open={releaseOpen}
              title={t('orders:so.detail.release.title')}
              description={t('orders:so.detail.release.description')}
              submitText={t('orders:so.detail.release.submit')}
              cancelText={t('common:actions.cancel')}
              reasonLabel={t('orders:so.detail.release.reasonLabel')}
              requiredMessage={t('orders:so.detail.release.reasonRequired')}
              reasonPlaceholder={t('orders:so.detail.release.reasonPlaceholder')}
              onSubmit={handleRelease}
              onCancel={() => setReleaseOpen(false)}
            />
          </>
        )}
      </Spin>
    </PageContainer>
  );
};

export default SalesOrderDetailPage;
